import React, { useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Map, Trophy, WifiOff } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useCabinRoadmap } from '../hooks/cabin/useCabinRoadmap';
import { useCabinStats } from '../hooks/cabin/useCabinStats';
import { interpolate } from '../utils/cabinI18n';
import ThemeStageHeader from '../components/cabin/ThemeStageHeader';
import ModuleCard from '../components/cabin/ModuleCard';
import ModuleDetailSheet from '../components/cabin/ModuleDetailSheet';
import MilesBadge from '../components/cabin/MilesBadge';

const pageVariants = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
};

const stageContentVariants = {
  hidden: { opacity: 0, height: 0, overflow: 'hidden' },
  visible: { opacity: 1, height: 'auto', overflow: 'visible' },
};

export default function CabinRoadmap() {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const shouldReduce = useReducedMotion();

  const { data: roadmap, isLoading, isError, refetch } = useCabinRoadmap();
  const { data: stats } = useCabinStats();

  const [selectedModule, setSelectedModule] = useState(null);
  // Track which stage sections are expanded (default: expand the active one)
  const [expandedStages, setExpandedStages] = useState({});

  const toggleStage = (stageId) => {
    setExpandedStages((prev) => ({ ...prev, [stageId]: !prev[stageId] }));
  };

  // Auto-expand the active stage on first load
  React.useEffect(() => {
    if (roadmap?.stages) {
      const initial = {};
      roadmap.stages.forEach((s) => {
        if (s.status === 'active' || s.status === 'completed') {
          initial[s.id] = true;
        }
      });
      setExpandedStages(initial);
    }
  }, [roadmap?.stages?.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleModuleNavigate = (mod) => {
    setSelectedModule(null);
    navigate(`/programs/kabin-memurlugu/modules/${mod.slug}`);
  };

  const totalMiles = stats?.totalMiles || 0;
  const overallProgress = roadmap?.overallProgress || 0;
  const totalModules = roadmap?.totalModules || 0;
  const completedModules = roadmap?.completedModules || 0;

  if (isError && !isLoading) {
    return (
      <div className="p-6 flex flex-col items-center gap-4 min-h-[60vh] justify-center">
        <WifiOff size={48} className="text-[var(--brand-danger)]" aria-hidden="true" />
        <h2 className="text-lg font-black text-[var(--text-main)]">{t('cabin.roadmap.error.title')}</h2>
        <p className="text-sm text-[var(--text-muted)]">{t('cabin.roadmap.error.sub')}</p>
        <button
          onClick={() => refetch()}
          className="px-5 py-2 bg-[var(--brand-primary)] text-white font-bold rounded-xl
                     shadow-[0_4px_0_0_var(--brand-primary-shadow)]
                     hover:bg-[var(--brand-primary-hover)] transition-colors
                     focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)]"
        >
          {t('cabin.error.cta')}
        </button>
      </div>
    );
  }

  return (
    <motion.div
      variants={shouldReduce ? {} : pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.25, ease: 'easeOut' }}
      className="min-h-screen"
    >
      {/* ── Hero Banner ── */}
      <div className="bg-gradient-to-r from-sky-600 via-blue-600 to-indigo-700 px-4 pt-6 pb-14 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <Map size={28} className="text-white shrink-0" aria-hidden="true" />
              <div>
                <h1 className="text-2xl font-black text-white">{t('cabin.roadmap.title')}</h1>
                <p className="text-sm text-sky-200">{t('cabin.roadmap.subtitle')}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Trophy size={16} className="text-yellow-300" aria-hidden="true" />
              <MilesBadge miles={totalMiles} />
            </div>
          </div>

          {/* Overall progress bar */}
          <div className="mt-4">
            <div className="flex justify-between items-center mb-1.5">
              <span className="text-xs text-sky-200 font-medium">
                {t('cabin.roadmap.hero.overall_progress')}
              </span>
              <span className="text-xs text-sky-100 font-bold">
                {interpolate(t('cabin.roadmap.modules_count'), {
                  completed: completedModules,
                  total: totalModules,
                })}
              </span>
            </div>
            <div className="h-2.5 bg-white/20 rounded-full overflow-hidden">
              <div
                className="h-full bg-white rounded-full transition-all duration-700"
                style={{ width: `${overallProgress}%` }}
              />
            </div>
          </div>

          {/* Stage chips — theme names (NO CEFR labels) */}
          {roadmap?.stages && (
            <div className="flex items-center gap-2 mt-4 flex-wrap">
              {roadmap.stages.map((stage) => {
                const name = lang === 'tr' ? stage.name_tr : stage.name_en;
                return (
                  <button
                    key={stage.id}
                    onClick={() => stage.status !== 'locked' && toggleStage(stage.id)}
                    className={`
                      flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold
                      transition-colors min-h-[36px]
                      ${stage.status === 'completed'
                        ? 'bg-green-500/20 text-green-200 border border-green-400/30 hover:bg-green-500/30'
                        : stage.status === 'active'
                        ? 'bg-amber-400/20 text-amber-200 border border-amber-400/30 hover:bg-amber-400/30'
                        : 'bg-white/10 text-sky-300 border border-white/20 cursor-default'}
                    `}
                    aria-label={`${name} ${stage.status !== 'locked' ? `· ${stage.progress}%` : ''}`}
                  >
                    <span aria-hidden="true">
                      {stage.status === 'locked' ? '🔒' : stage.theme_icon}
                    </span>
                    <span>{name}</span>
                    {stage.status !== 'locked' && (
                      <span className="opacity-75">· {stage.progress}%</span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── Timeline Content ── */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 -mt-6 relative z-10">
        {isLoading ? (
          /* Loading skeleton */
          <div className="flex flex-col gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="flex gap-4 mb-4">
                  <div className="w-14 h-14 bg-gray-200 rounded-2xl shrink-0" />
                  <div className="flex-1">
                    <div className="h-5 bg-gray-200 rounded w-1/3 mb-2" />
                    <div className="h-3 bg-gray-200 rounded w-1/2 mb-3" />
                    <div className="h-2 bg-gray-200 rounded-full w-full" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {[1, 2, 3].map((j) => (
                    <div key={j} className="h-40 bg-gray-200 rounded-2xl" />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-6">
            {(roadmap?.stages || []).map((stage, idx) => {
              const isExpanded = !!expandedStages[stage.id];
              const isLast = idx === (roadmap.stages.length - 1);

              return (
                <div key={stage.id} className="relative">
                  {/* Vertical connector line */}
                  {!isLast && (
                    <div
                      className={`absolute left-7 top-full w-0.5 h-6 -mt-0 z-0 transition-colors duration-500 ${
                        stage.status === 'completed' ? 'bg-green-300' : 'bg-gray-200'
                      }`}
                      aria-hidden="true"
                    />
                  )}

                  {/* Stage header */}
                  <ThemeStageHeader
                    stage={stage}
                    isExpanded={isExpanded}
                    onToggle={() => toggleStage(stage.id)}
                  />

                  {/* Module cards — collapsible */}
                  <AnimatePresence initial={false}>
                    {isExpanded && stage.status !== 'locked' && (
                      <motion.div
                        key={`${stage.id}-content`}
                        initial={shouldReduce ? false : 'hidden'}
                        animate="visible"
                        exit="hidden"
                        variants={shouldReduce ? {} : stageContentVariants}
                        transition={{ duration: 0.25, ease: 'easeInOut' }}
                      >
                        {stage.modules.length > 0 ? (
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
                            {stage.modules.map((mod) => (
                              <ModuleCard
                                key={mod.id}
                                module={mod}
                                onClick={() => setSelectedModule(mod)}
                              />
                            ))}
                          </div>
                        ) : (
                          <div className="mt-4 p-4 rounded-xl bg-gray-50 border border-gray-200 text-center">
                            <p className="text-sm text-[var(--text-muted)]">
                              {lang === 'tr' ? 'Bu temada modül yok.' : 'No modules in this theme.'}
                            </p>
                          </div>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ModuleDetailSheet
        module={selectedModule}
        open={!!selectedModule}
        onClose={() => setSelectedModule(null)}
        onNavigate={handleModuleNavigate}
      />
    </motion.div>
  );
}
